import { Container } from "inversify";
import { version } from "../package.json";
import { Logger } from "./entities/Logger";
import type { IModule } from "./entities/Module";
import { DomElementsService } from "./services/DomElementsService";

/**
 * Default API exposed by the engine
 */
type DefaultApi = { version: string; dispose: () => void };

/**
 * Infers the concrete return type of the `api` getter from a module instance.
 */
type ApiOf<M> = M extends { readonly api: infer R } ? R : object;

type EngineOptions = {
	wrapperElement: HTMLDivElement;
};

/**
 * @name Engine
 * @description Composable engine builder.
 * Registers modules via `.use()`, then `.build()` wires everything up.
 * `TApi` accumulates the merged API type from all registered modules (starts as `DefaultApi`)
 *
 * @example
 * const api = new Engine({ wrapperElement })
 *   .use(new CoreModule({ palette: APP_PALETTE }))
 *   .use(new Workspace3DModule({ rendererService: CustomRenderer }))
 *   .use(new MapLibreModule())
 *   .build();
 *
 * @see README.md#102
 */
class Engine<TApi = DefaultApi> {
	#logger = new Logger("Engine", { color: "#ffffff", backgroundColor: "#ff0000" });
	#container = new Container();
	#modules: Array<IModule> = [];
	#domElementsService: DomElementsService;

	constructor({ wrapperElement }: EngineOptions) {
		this.#domElementsService = new DomElementsService(wrapperElement);
		this.#container.bind(DomElementsService).toConstantValue(this.#domElementsService);

		this.#logger.log("CONSTRUCTED");
	}

	/**
	 * Register a module instance.
	 * Configuration and dependency overrides are handled by the module's own constructor.
	 */
	public use<M extends IModule>(module: M): Engine<TApi & ApiOf<M>> {
		this.#modules.push(module);
		return this as unknown as Engine<TApi & ApiOf<M>>;
	}

	public build(): TApi {
		this.#logger.log("Building engine with modules");

		this.#domElementsService.initialize();

		// Phase 1: Let every module register its service bindings.
		for (const module of this.#modules) {
			module.register(this.#container);
		}

		// Phase 2: Initialize all modules (all bindings now available).
		for (const module of this.#modules) {
			module.initialize(this.#container);
		}

		const apis = this.#modules.map((module) => module.api);

		this.#logger.log("BUILT");

		const coreApi: DefaultApi = { version, dispose: this.dispose };
		return Object.assign(coreApi, ...apis) as TApi;
	}

	private dispose = () => {
		for (let i = this.#modules.length - 1; i >= 0; i--) {
			this.#modules[i].dispose();
		}

		this.#domElementsService.dispose();
		this.#container.unbindAll();

		this.#logger.log("DISPOSED");
	};
}

export { type ApiOf, Engine };
